import i18n from "../../../i18n";
import { useState, useCallback, useEffect } from "react";
import { CouplePatient, CreateCouplePatientDTO } from "../domain/CouplePatient";
import { CouplePatientAPI } from "../infrastructure/api/CouplePatientApiService";

export const useCouplePatient = (id?: string) => {
    const [couple, setCouple] = useState<CouplePatient | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const getCouple = useCallback(async (coupleId: string) => {
        setLoading(true);
        setError(null);
        try {
            const response = await CouplePatientAPI.get(coupleId);
            setCouple(response.data);
            return response.data;
        } catch (err) {
            console.error(err);
            setError("Error loading couple patient");
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const createCouple = useCallback(async (data: CreateCouplePatientDTO) => {
        setLoading(true);
        setError(null);
        try {
            const response = await CouplePatientAPI.create(data);
            setCouple(response.data);
            return response.data;
        } catch (err) {
            console.error(err);
            const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data
                ?.detail;
            // One partner the centre's and the other not: say so, since the
            // fix is choosing two who match.
            setError(
                detail === "couple_mixed_ownership"
                    ? i18n.t("patient.ownership.coupleMixed")
                    : "Error creating couple patient"
            );
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteCouple = useCallback(async () => {
        if (!id) return false;
        setLoading(true);
        try {
            await CouplePatientAPI.delete(id);
            return true;
        } catch (err) {
            console.error(err);
            setError("Error deleting couple patient");
            return false;
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            getCouple(id);
        }
    }, [id, getCouple]);

    return { couple, loading, error, getCouple, createCouple, deleteCouple };
};
